/* eslint-disable react/prop-types */
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Box, Button, LinearProgress, Tab, Tabs, TextField, Typography, Zoom } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import noop from 'lodash/noop';

import CustomerStatusLabel from '../CustomerStatusLabel';
import CustomPrompt from './CustomPrompt';
import Icons from '../../Icons';
import ScrollingTabs from '../ScrollingTabs';
import localization from '../../../localization';
import LoadingErrorNotification from '../LoadingErrorNotification';
import SelectCustomerNotification from '../SelectCustomerNotification';
import { copyText } from '../../../services/helpers/utils';
import permissions from '../../../services/permissions';
import { CRUD_RESTRICTION } from '../../../services/constants/permissions';
import { CRUD_ENDPOINTS, RESOURCE_URL } from '../../../screens/CrudHelperScreen/constants';
import parentPaths from '../../../services/paths';
import { SERVER } from '../../../axios';

import './detailPageWrapper.scss';

const DetailPageWrapper = ({
  nxStateNotNeeded,
  children,
  nxState,
  id,
  name,
  sectionName,
  saveIsDisabled,
  hasChanges = false,
  isLoading,
  curData,
  addFunc,
  updateFunc,
  beforeSend,
  setUpdate,
  setLoading,
  extraActions,
  noTabsMargin,
  customSave,
  headerTitleCopy,
  tabs,
  errors,
  parentId,
  sectionRefs,
  setBackToParent,
  selectedSection,
  setSelectedSection,
  editableHead,
  onHeadEdit,
  afterNameComponent,
}) => {
  const location = useLocation();
  const [isEditingHead, setIsEditingHead] = useState(false);
  const [isPostRequest, setPostRequest] = useState(false)

  const customerData = useSelector(({ tableData: { customerName } }) => customerName);

  const isNew = id === 'add';

  const history = useHistory();

  const linkCRUD = useMemo(() => {
    // check crud button restrictions
    if (!permissions.checkRestriction(CRUD_RESTRICTION)) return null;

    if (isNew) return null;

    const endpoint = CRUD_ENDPOINTS.find(({ path }) => Array.isArray(path)
      ? path.some((item) => location.pathname.includes(item))
      : location.pathname.includes(path));

    return endpoint
      ? {
        pathname: parentPaths.crudHelper.request,
        search: `${RESOURCE_URL}=${SERVER}${endpoint.id}/${id}`,
      }
      : null;
  }, [id, location.pathname]);

  const toggleEditHead = () => setIsEditingHead((prev) => !prev);

  const handleSave = () => {
    if (customSave) {
      customSave();
      setLoading(true);
    } else {
      const sendObj = beforeSend ? beforeSend(curData) : curData;

      if (isNew) {
        // beforeunload never wait for POST-requests
        // we should wait for update hasChanges props
        setPostRequest(true);
        addFunc(sendObj).then((res) => {
          const headersLocation = res.headers.location.split('/');
          const newId = headersLocation[headersLocation.length - 1];
          const detailsPath = location.pathname.split('/add')[0];

          toast(localization.t('general.updatesHaveBeenSaved'));

          history.push(`${detailsPath}/${newId}`);

          setUpdate((u) => u + 1);
          setPostRequest(false);
        });
      } else {
        updateFunc(id, sendObj).then(() => {
          toast(localization.t('general.updatesHaveBeenSaved'));
          setUpdate((u) => u + 1);
        });
      }
    }
  };

  if (isLoading) return <LinearProgress />;

  if (!isLoading && !curData) return <LoadingErrorNotification />;

  if (isNew && !nxState?.selectedCustomer?.id && !nxStateNotNeeded && !location?.state?.scheduledPriceCustomerId) {
    return <SelectCustomerNotification />;
  }

  const handleChange = (event, newValue) => tabs?.setCurTab(newValue);

  return (
    curData && (
      <>
        <CustomPrompt when={!isPostRequest && hasChanges} />
        <Box>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" alignItems="center">
              {headerTitleCopy && (
                <ContentCopyIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    copyText(headerTitleCopy);
                  }}
                  color="secondary"
                  style={{ marginRight: '5px', fontSize: '20px' }}
                  className="copyIcon"
                />
              )}
              {sectionName && (
                <Typography
                  data-test="notificationName"
                  gutterBottom
                  variant="h3"
                  style={{ fontSize: '20px' }}
                  mb={0}
                  mr="7px"
                >
                  {sectionName}
                </Typography>
              )}
              {editableHead && !isEditingHead && (
                <Box mr="7px" className="iconButton">
                  <Icons.EditIcon
                    onClick={toggleEditHead}
                    color="secondary"
                  />
                </Box>
              )}
              {!isEditingHead
                ? (
                  <Typography
                    data-test="notificationName"
                    gutterBottom
                    variant="h3"
                    style={{ fontSize: '20px' }}
                    mb={0}
                  >
                    {name}
                  </Typography>
                )
                : (
                  <TextField
                    autoFocus
                    onBlur={toggleEditHead}
                    onChange={onHeadEdit}
                    inputProps={{
                      style: {
                        fontSize: '20px',
                        fontWeight: 500,
                        position: 'relative',
                        top: '-1px',
                        letterSpacing: '-0.06px',
                      },
                    }}
                    value={name}
                  />
                )}
              {customerData && (
                <Box width={1} marginLeft="12px">
                  {customerData && <CustomerStatusLabel customer={customerData} />}
                </Box>
              )}
              {afterNameComponent}
            </Box>
            <Box display="flex" gap={2}>
              <Zoom in={hasChanges}>
                <Button
                  disabled={saveIsDisabled}
                  id="save-notification-button"
                  color="primary"
                  size="large"
                  type="submit"
                  variant="contained"
                  onClick={handleSave}
                >
                  {localization.t('general.save')}
                </Button>
              </Zoom>
              {extraActions}
              {!!linkCRUD && (
                <Button
                  variant="outlined"
                  sx={{ minWidth: '125px' }}
                  component={Link}
                  to={linkCRUD}
                >
                  {localization.t('general.crud')}
                </Button>
              )}
            </Box>
          </Box>
          {sectionRefs && (
            <ScrollingTabs
              setBackToParent={setBackToParent}
              parentId={parentId}
              sectionRefs={sectionRefs}
              errors={errors}
              selectedSection={selectedSection}
              setSelectedSection={setSelectedSection}
            />
          )}

          {tabs?.tabLabels && (
            <Tabs
              value={tabs?.curTab}
              indicatorColor="primary"
              textColor="primary"
              onChange={handleChange}
              aria-label="disabled tabs example"
            >
              {tabs?.tabLabels.map((tab) => (
                <Tab
                  style={errors?.[tab] ? { color: '#ff6341' } : {}}
                  key={tab}
                  label={localization.t(`labels.${tab}`)}
                />
              ))}
            </Tabs>
          )}
        </Box>
        <Box overflow="auto" mt={noTabsMargin ? 0 : 2} p="2px" flexGrow={1} display="block">
          {children}
        </Box>
      </>
    )
  );
};

DetailPageWrapper.propTypes = {
  children: PropTypes.node,
  nxState: PropTypes.object,
  id: PropTypes.string,
  saveIsDisabled: PropTypes.bool,
  hasChanges: PropTypes.bool,
  name: PropTypes.string,
  isLoading: PropTypes.bool,
  curData: PropTypes.object,
  addFunc: PropTypes.func,
  updateFunc: PropTypes.func,
  setUpdate: PropTypes.func,
  setLoading: PropTypes.func,
  beforeSend: PropTypes.func,
  nxStateNotNeeded: PropTypes.bool,
  extraActions: PropTypes.node,
  tabs: PropTypes.object,
  customSave: PropTypes.func,
  headerTitleCopy: PropTypes.any,
  sectionRefs: PropTypes.any,
  setBackToParent: PropTypes.func,
  editableHead: PropTypes.bool,
  onHeadEdit: PropTypes.func,
};

DetailPageWrapper.defaultProps = {
  onHeadEdit: noop,
};

export default DetailPageWrapper;
